import { useEffect, useState } from "react";
import { Loader2, Megaphone, X } from "lucide-react";
import { papi } from "../portalApi";
import { NewsMedia } from "./NewsMedia";
import { NewsReact } from "./NewsReact";

const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const NewsItem = ({ n }) => (
  <article className={`rounded-xl border bg-white p-4 ${n.seen ? "" : "border-teal-300 ring-1 ring-teal-100"}`} data-testid={`news-item-${n.id}`}>
    <div className="flex items-center gap-2">
      <Megaphone className="h-4 w-4 text-teal-600" />
      <h3 className="flex-1 font-semibold text-slate-900">{n.title || "Message from the office"}</h3>
      {!n.seen && <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold text-white">NEW</span>}
      <span className="text-[11px] text-slate-400">{when(n.created_at)}</span>
    </div>
    {n.text && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{n.text}</p>}
    {!!n.files?.length && <div className="mt-3 grid gap-2 md:grid-cols-2">{n.files.map((f, i) => <NewsMedia key={i} bid={n.id} n={i} f={f} />)}</div>}
    <NewsReact n={n} />
  </article>
);

/* News tab: everything the office has sent; opening it marks every message as seen. */
export const PortalNews = ({ onSeen }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    papi("get", "/news").then(async (r) => {
      setD(r);
      await Promise.all(r.news.filter((x) => !x.seen || x.new_replies).map((x) => papi("post", `/news/${x.id}/seen`).catch(() => {})));
      onSeen?.();
    }).catch(() => setD({ news: [] }));
  }, []);
  if (!d) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-teal-600" />;
  return (
    <div className="space-y-3" data-testid="portal-news">
      <h2 className="text-lg font-bold text-slate-900">News from the office</h2>
      {d.news.map((n) => <NewsItem key={n.id} n={n} />)}
      {!d.news.length && <p className="rounded-xl border bg-white p-6 text-center text-sm text-slate-400" data-testid="news-empty">No messages yet.</p>}
    </div>
  );
};

/* Banner over the Hub until the partner opens the message (or taps Later; a re-send shows it again). */
export const NewsBanner = ({ items, onOpen, onChanged }) => {
  if (!items?.length) return null;
  const n = items[0];
  const later = () => papi("post", `/news/${n.id}/later`).finally(onChanged);
  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-teal-300 bg-gradient-to-r from-teal-50 to-white p-3 shadow-sm" data-testid="news-banner">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-teal-600 text-white"><Megaphone className="h-4 w-4 animate-pulse" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{n.title || "New message from the office"}{n.resent_at ? <span className="ml-2 rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">REMINDER</span> : null}</p>
        <p className="truncate text-xs text-slate-600">{n.text || `${n.files?.length || 0} attachment(s)`}{items.length > 1 ? ` · +${items.length - 1} more` : ""}</p>
      </div>
      <button onClick={onOpen} className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700" data-testid="news-banner-open">Open</button>
      <button onClick={later} className="rounded-lg p-1.5 text-slate-400 hover:bg-white" title="Later" data-testid="news-banner-later"><X className="h-4 w-4" /></button>
    </div>
  );
};
